import { useEffect, useState } from 'react'
import { X } from 'lucide-react'
import { useI18n } from '../i18n/useI18n'
import { showToast } from './Toast'
import { api, ApiError } from '../services/api'
import type { Reservation, Store } from '../store/useStore'
import { apiErrorI18nKey, apiErrorRef } from '../lib/apiErrorMessage'
import { bookableRange, type BookableConfig } from '../lib/bookableRange'
import { reservationPaymentRules } from '../lib/storePayment'
import { useCourse } from '../hooks/useCourses'
import MonthCalendar from './MonthCalendar'

interface Props {
  open: boolean
  onClose: () => void
  reservation: Reservation
  storeName: string
}

/**
 * #745 Phase 2.2: 予約日時を変更するモーダル.
 *
 * フロー:
 *  1. 日付選択 (= 月カレンダー。範囲は新規予約と同じく店の「受け付ける期間」・定休日・
 *     前払いの最短日で決まる #2020。以前は 14 日先まで固定で、店が 60 日先まで受けて
 *     いても 14 日分しか出ず、今の予約日より後ろにずらせなかった)
 *  2. 時刻選択 (= getReservationSlots で空きスロットを fetch、自分自身は除外する想定で
 *     server も自分を除外しているので safe)
 *  3. 任意の理由 → submit
 *  4. status は 'pending' に戻り店主再承認待ち。決済は持ち越し。
 */
export default function ChangeDatetimeModal({ open, onClose, reservation, storeName }: Props) {
  const { t } = useI18n()
  const [newDate, setNewDate] = useState<string>(reservation.date)
  const [newTime, setNewTime] = useState<string>('')
  const [slots, setSlots] = useState<string[]>([])
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [slotsFailed, setSlotsFailed] = useState<unknown>(null)
  const [reason, setReason] = useState('')
  const [submitting, setSubmitting] = useState(false)
  // #2020 店の受付設定。開いたときに読む。読めるまではカレンダーを出さない (読める前に
  // 押した日が、読めた後に不可になっても選ばれたまま残るため)。取れなければ既定
  // (30 日・毎日) で出し、サーバの判定に任せる。
  const [config, setConfig] = useState<BookableConfig | null | 'failed'>(null)
  // コースは一覧 (有効なものだけ) ではなく単体で引く。店が後からコースを無効にした予約でも、
  // サーバはそのコースの最短日で弾くので、画面も同じコースで範囲を出す
  const { course, loading: courseLoading } = useCourse(reservation.storeId, reservation.courseId)

  // open 切替で state リセット
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (open) {
      setNewDate(reservation.date)
      setNewTime('')
      setSlots([])
      setSlotsFailed(null)
      setReason('')
    }
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setConfig(null)
    api.getStore(reservation.storeId)
      .then((store: Store) => {
        if (cancelled) return
        // #2026 締切 (前払いの最短 3 日前) は**この予約の**支払い方法で出す。予約の後に店が
        // 支払い方法や「現金のみ」を切り替えても、この予約の支払い方法は変わらない
        // (サーバは既存の予約を解決し直さない)。店の保存値で出すと、当日払いで取った
        // 予約にも前払いの締切がかかる
        setConfig(store?.reservationConfig
          ? {
              ...store.reservationConfig,
              paymentTiming: reservation.paymentTiming ?? reservationPaymentRules(store).paymentTiming,
            }
          : 'failed')
      })
      .catch(err => {
        // 範囲が分からなくても変更はできる (既定の範囲で出す)。サーバが最終判定
        console.error('[ChangeDatetime] store config load failed:', err)
        if (!cancelled) setConfig('failed')
      })
    return () => {
      cancelled = true
    }
  }, [open, reservation.storeId, reservation.paymentTiming])

  useEffect(() => {
    if (!open || !newDate) return
    let cancelled = false
    setLoadingSlots(true)
    // #1386 既存予約の人数で絞り込む (変更後も同じ人数が入れる枠だけ出す)
    api.getReservationSlots(
      reservation.storeId, newDate, reservation.courseId, reservation.partySize,
    )
      .then(res => {
        // **成功したら失敗状態を消す。** 消さないと一度 500 を踏んだ後、
        // モーダルを閉じるまで時刻を選べないままになる
        if (!cancelled) { setSlots(res.slots || []); setSlotsFailed(null) }
      })
      .catch(err => {
        console.error('[ChangeDatetime] slots load failed:', err)
        // 取得失敗を「空きなし」と混同させない (#1624 の議論)
        if (!cancelled) { setSlots([]); setSlotsFailed(err) }
      })
      .finally(() => {
        if (!cancelled) setLoadingSlots(false)
      })
    return () => {
      cancelled = true
    }
  }, [open, newDate, reservation.storeId, reservation.courseId, reservation.partySize])

  if (!open) return null

  // #2020 範囲は新規予約と同じ計算。店の設定が取れなかったときだけ既定 (30 日・毎日)
  const rangeReady = config !== null && !courseLoading
  const range = bookableRange(
    config === null || config === 'failed'
      ? { availableDays: [0, 1, 2, 3, 4, 5, 6], paymentTiming: reservation.paymentTiming ?? undefined }
      : config,
    course,
  )

  const isUnchanged = newDate === reservation.date && newTime === reservation.time
  const canSubmit = !!newTime && !isUnchanged

  const handleSubmit = async () => {
    if (submitting || !canSubmit) return
    setSubmitting(true)
    try {
      await api.changeReservationDatetime(reservation.id, newDate, newTime, reason.trim() || undefined)
      showToast({ type: 'success', message: t('changeDatetime.updated') })
      onClose()
    } catch (e) {
      const detail = (e as Error)?.message || ''
      // 管理番号040: 締切超過は構造化 detail (code) で返る。400 の message は
      // apiFetch が汎用文言に差し替えるので、message の文字列一致では判定できない。
      const code = (e as ApiError)?.code
      console.error('[ChangeDatetime] failed:', e)
      let msg = t('changeDatetime.failed')
      if (code === 'change_deadline_passed') msg = t('changeDatetime.deadlinePassed')
      else if (code === 'change_pending') msg = t('myReservations.changeLockedPending')
      else if (code === 'new_datetime_too_soon') msg = t('changeDatetime.tooSoon')
      else if (code === 'beyond_bookable_window') msg = t('changeDatetime.beyondWindow')
      else if (detail.includes('taken')) msg = t('changeDatetime.slotTaken')
      else if (detail.includes('not available')) msg = t('changeDatetime.dayUnavailable')
      showToast({ type: 'error', message: msg })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 px-4 animate-fade-in">
      <div className="w-full max-w-sm rounded-2xl bg-white p-5 max-h-[85vh] overflow-y-auto animate-scale-in">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg font-bold text-text-primary">
            {t('changeDatetime.title')}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 tap-effect"
            aria-label="close"
          >
            <X size={16} className="text-text-secondary" />
          </button>
        </div>

        <p className="font-body text-xs text-text-tertiary mb-1">{storeName}</p>
        <p className="font-body text-xs text-text-tertiary mb-4">
          {t('changeDatetime.previous', { date: reservation.date, time: reservation.time })}
        </p>

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('changeDatetime.newDate')}
        </p>
        <div className="mb-4">
          {rangeReady ? (
            <MonthCalendar
              value={newDate}
              onChange={d => {
                setNewDate(d)
                setNewTime('')
              }}
              maxDate={range.maxDate}
              isEnabled={range.isEnabled}
            />
          ) : (
            <div className="h-64 animate-pulse rounded-xl bg-gray-100" aria-busy="true" />
          )}
        </div>

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('changeDatetime.newTime')}
        </p>
        {loadingSlots ? (
          <p className="text-xs text-text-tertiary font-body mb-4">...</p>
        ) : slotsFailed ? (
          <div className="mb-4 flex flex-col items-start gap-1">
            <p className="font-body text-xs text-text-secondary">{t(apiErrorI18nKey(slotsFailed))}</p>
            <p className="font-body text-[10px] text-text-tertiary">{apiErrorRef(slotsFailed)}</p>
          </div>
        ) : slots.length === 0 ? (
          <p className="text-xs text-text-tertiary font-body mb-4">{t('changeDatetime.noSlots')}</p>
        ) : (
          <div className="flex flex-wrap gap-1.5 mb-4">
            {slots.map(s => (
              <button
                key={s}
                onClick={() => setNewTime(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-body font-medium transition-colors tap-effect ${
                  newTime === s ? 'bg-navy text-white' : 'bg-gray-100 text-text-primary'
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        <p className="font-body text-sm font-semibold text-text-primary mb-2">
          {t('changeDatetime.reasonLabel')}
        </p>
        <textarea
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder={t('changeDatetime.reasonPlaceholder')}
          rows={2}
          className="w-full bg-gray-50 rounded-lg px-3 py-2 font-body text-sm text-text-primary outline-none focus:ring-2 focus:ring-yokoso-red/30 mb-4 resize-none"
        />

        <p className="font-body text-xs text-text-secondary leading-relaxed mb-4 bg-gray-50 rounded p-2.5">
          {t('changeDatetime.note')}
        </p>

        <div className="flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 h-11 rounded-xl border border-gray-200 text-sm font-body font-semibold text-text-secondary"
          >
            {t('changeDatetime.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
            className={`flex-1 h-11 rounded-xl text-sm font-body font-semibold transition-colors ${
              !canSubmit || submitting ? 'bg-gray-300 text-white' : 'bg-yokoso-red text-white tap-effect'
            }`}
          >
            {submitting ? '...' : t('changeDatetime.confirm')}
          </button>
        </div>
      </div>
    </div>
  )
}
